import useBodyScrollDisable from '#hooks/useBodyScrollDisable';
import { AnimatePresence } from 'framer-motion';
import { PropsWithChildren, ReactNode } from 'react';
import Icon from '../Icon';
import Portal from '../Portal';
import * as S from './styles';

export interface BottomSheetProps extends PropsWithChildren {
  isOpen: boolean;
  closableBackground?: boolean;
  fullHeight?: boolean;
  confirmButton?: ReactNode;
  handleClose: () => void;
  onClickConfirm?: () => void;
  showHeader?: boolean;
  bodyStyle?: React.CSSProperties;
}

const backgroundVariants = {
  open: { opacity: 0.4 },
  closed: { opacity: 0 }
};

const wrapperVariants = {
  open: { y: 0, opacity: 1 },
  closed: { y: '100%', opacity: 0 }
};

export default function BottomSheet({ isOpen, ...props }: BottomSheetProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <Portal portalId="bottom-sheet">
      <AnimatePresence>
        {isOpen && <BottomSheetContainer {...props} />}
      </AnimatePresence>
    </Portal>
  );
}

function BottomSheetContainer({
  children,
  fullHeight,
  confirmButton,
  onClickConfirm,
  handleClose,
  closableBackground,
  showHeader = true,
  bodyStyle
}: Omit<BottomSheetProps, 'isOpen'>) {
  useBodyScrollDisable();

  const handleClickBackground = () => {
    if (closableBackground) {
      handleClose();
    }
  };

  return (
    <S.Wrapper>
      <S.Background
        initial="closed"
        animate="open"
        exit="closed"
        variants={backgroundVariants}
        transition={{ duration: 0.1 }}
        onClick={handleClickBackground}
      />
      <S.BottomSheetWrapper
        initial="closed"
        animate="open"
        exit="closed"
        variants={wrapperVariants}
        transition={{ duration: 0.3 }}
        fullHeight={fullHeight}
      >
        {showHeader && (
          <S.BottomSheetHeader>
            <button onClick={handleClose}>
              <Icon name="IconClose" />
            </button>
            {confirmButton && (
              <S.ButtonConfirm onClick={onClickConfirm}>
                {confirmButton}
              </S.ButtonConfirm>
            )}
          </S.BottomSheetHeader>
        )}

        <S.BottomSheetBody style={bodyStyle}>{children}</S.BottomSheetBody>
      </S.BottomSheetWrapper>
    </S.Wrapper>
  );
}
